<template>
  <v-card>
    <v-card-title>
      <v-icon left>mdi-form-textbox</v-icon>
      Manual Data Entry
    </v-card-title>

    <v-card-text>
      <v-form ref="form" v-model="valid">
        <v-row>
          <!-- kVA Demand Input (Electricity Mode Only) -->
          <v-col cols="12" md="6" v-if="showSingleKvaDemandInput">
            <v-text-field
              v-model.number="formData.kvaDemand"
              label="kVA Demand"
              :type="inputConfig.kvaDemand.type"
              :step="inputConfig.kvaDemand.step"
              :min="inputConfig.kvaDemand.min"
              prepend-icon="mdi-flash"
              :suffix="inputConfig.kvaDemand.suffix"
              :rules="kvaDemandRules"
              outlined
              @input="validateAndEmit"
            />
          </v-col>

          <!-- Actual Bill Amount Input (Electricity Mode Only) -->
          <v-col cols="12" md="6" v-if="!isDGMode">
            <v-text-field
              v-model.number="formData.actualBillAmount"
              label="Actual Bill Amount"
              :type="inputConfig.actualBillAmount.type"
              :step="inputConfig.actualBillAmount.step"
              :min="inputConfig.actualBillAmount.min"
              prepend-icon="mdi-currency-usd"
              :suffix="$currencySymbol"
              :rules="actualBillAmountRules"
              outlined
              @input="validateAndEmit"
            />
          </v-col>

          <!-- Actual kWh from Bill Input (Electricity Mode Only) -->
          <v-col cols="12" md="6" v-if="!isDGMode">
            <v-text-field
              v-model.number="formData.actualKwhFromBill"
              label="Actual kWh from Bill"
              type="number"
              step="0.1"
              min="0"
              prepend-icon="mdi-file-document-outline"
              suffix="kWh"
              :rules="actualKwhRules"
              outlined
              hint="Enter the kWh consumption shown on the physical electricity bill"
              persistent-hint
              @input="validateAndEmit"
            />
          </v-col>

          <!-- kVA Demand Charges Input -->
          <v-col
          cols="12"
          md="6"
          v-if="showManualKvaDemandCharges">
            <v-text-field
              v-model.number="formData.kvaDemandCharges"
              label="kVA Demand Charges"
              type="number"
              step="0.01"
              min="0"
              prepend-icon="mdi-currency-usd"
              :suffix="$currencySymbol"
              :rules="nonNegativeRules"
              outlined
              @input="validateAndEmit"
            />
          </v-col>

          <!-- Diesel Consumption Input (DG Mode Only) -->
          <v-col cols="12" md="6" v-if="isDGMode">
            <v-text-field
              v-model.number="formData.dieselConsumption"
              label="Diesel Consumption"
              :type="inputConfig.dieselConsumption.type"
              :step="inputConfig.dieselConsumption.step"
              :min="inputConfig.dieselConsumption.min"
              prepend-icon="mdi-gas-station"
              :suffix="inputConfig.dieselConsumption.suffix"
              :rules="dieselConsumptionRules"
              outlined
              @input="validateAndEmit"
            />
          </v-col>

          <!-- Running Hours Input (DG Mode Only) -->
          <v-col cols="12" md="6" v-if="isDGMode">
            <v-text-field
              v-model.number="formData.runningHours"
              label="Running Hours"
              :type="inputConfig.runningHours.type"
              :step="inputConfig.runningHours.step"
              :min="inputConfig.runningHours.min"
              prepend-icon="mdi-clock-outline"
              :suffix="inputConfig.runningHours.suffix"
              :rules="runningHoursRules"
              outlined
              @input="validateAndEmit"
            />
          </v-col>
        </v-row>

        <!-- Additional Information Section -->
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="formData.notes"
              label="Notes (Optional)"
              prepend-icon="mdi-note-text"
              rows="3"
              outlined
              :counter="inputConfig.notes.counter"
              :rules="notesRules"
              @input="validateAndEmit"
            />
          </v-col>
        </v-row>

        <!-- Time-of-use Demand and Adjustments -->
        <template v-if="showTimeOfUseDemandSplit">
          <v-divider class="my-4" />

          <div class="text-subtitle-1 font-weight-medium mb-3">
            <v-icon left color="info">mdi-flash</v-icon>
            MAXIMUM DEMAND
          </div>
          <v-alert type="info" dense outlined>
            Demand charges are calculated from the final demand values and the configured rates.
          </v-alert>

          <v-row>
            <v-col cols="12">
              <v-card outlined class="time-of-use-demand-card">
                <v-card-text class="pt-0">
                  <v-simple-table class="time-of-use-demand-table">
                    <template #default>
                      <thead>
                        <tr>
                          <th class="text-left">Demand Charges</th>
                          <th class="text-center">Final</th>
                          <th class="text-center">Demand Charges</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="line in timeOfUseDemandLines"
                          :key="line.key"
                        >
                          <td class="unit-label-cell">
                            <div class="font-weight-medium">
                              {{ line.label }}
                            </div>
                            <div class="caption text--secondary">
                              @B{{ formatTimeOfUseRate(line.rate) }} / KW
                            </div>
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="formData.timeOfUseDemand[line.key].kw"
                              type="number"
                              step="0.01"
                              min="0"
                              outlined
                              dense
                              hide-details
                              class="time-of-use-table-input"
                              @input="validateAndEmit"
                            />
                          </td>
                          <td class="text-center font-weight-bold">
                            {{ formatTimeOfUseDemandCharge(line.key) }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                  <v-divider class="my-4" />

                  <v-row align="center" class="time-of-use-total-row">
                    <v-col cols="12" md="8" class="py-0">
                      <div class="text-subtitle-2 font-weight-medium">
                        Maximum demand is captured in the final column above.
                      </div>
                      <div class="caption text--secondary">
                        On peak, off peak, and Sunday max demand are included.
                      </div>
                    </v-col>
                    <v-col cols="12" md="4" class="py-0 text-md-right">
                      <div class="time-of-use-total-chip">
                        TOTAL DEMAND CHARGES
                      </div>
                      <div class="text-h6 font-weight-bold mt-2">
                        {{ formattedTimeOfUseDemandChargeTotal }} {{$currencySymbol}}
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-alert type="info" dense outlined class="mb-4">
                Oil surcharge is pulled automatically from master
                data and applied using the formula
                <strong>total consumption units × rate</strong>.
                Billing unit cost is calculated automatically
                from the final subtotal and total consumption units,
                so you do not need to enter it manually.
                <span v-if="formattedOilSurchargeRate">
                  Current oil surcharge rate:
                  <strong>{{ formattedOilSurchargeRate }} {{ $currencySymbol }}/kWh</strong>
                </span>
              </v-alert>
            </v-col>
          </v-row>
        </template>

        <!-- Form Validation Status -->
        <v-row v-if="showValidationStatus">
          <v-col cols="12">
            <v-alert
              :type="valid ? 'success' : 'warning'"
              outlined
              dense
            >
              <div v-if="valid">
                <v-icon left>mdi-check-circle</v-icon>
                All required fields are valid
              </div>
              <div v-else>
                <v-icon left>mdi-alert-circle</v-icon>
                Please complete all required fields correctly
              </div>
            </v-alert>
          </v-col>
        </v-row>

      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="grey"
        text
        @click="resetForm"
      >
        Reset
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!valid"
        @click="submitForm"
      >
        <v-icon left>mdi-check</v-icon>
        Confirm Entry
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { VALIDATION_RULES, INPUT_CONFIG } from '../../config/validationConfig';
import costCategoryMixin from '../../mixins/costCategoryMixin';
import {
  buildTimeOfUseDemandLines,
  buildTimeOfUseUnitLines,
  getActiveChargeCodes,
} from '../../utils/chargeMaster';
import { resolveBillingMode, isTimeOfUseBillingMode } from '../../utils/billingMode';
import appConfig from '../../config/appConfig';

const MANUAL_KVA_DEMAND_CHARGE_CODES = [
  'KVA_DEMAND_CHARGES',
  'MANUAL_KVA_DEMAND_CHARGES',
];

export default {
  name: 'ManualDataEntryForm',
  mixins: [costCategoryMixin],

  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({
        kvaDemand: 0,
        actualBillAmount: 0,
        kvaDemandCharges: 0,
        actualKwhFromBill: 0,
        notes: '',
        // DG specific fields
        dieselConsumption: 0,
        runningHours: 0,
      }),
    },
    showValidationStatus: {
      type: Boolean,
      default: true,
    },
    consumptionData: {
      type: Object,
      default: null,
    },
    billingPeriod: {
      type: Object,
      default: null,
    },
    // New prop to indicate DG mode
    isDGMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: false,
      formData: {
        kvaDemand: 0,
        actualBillAmount: 0,
        kvaDemandCharges: 0,
        actualKwhFromBill: 0,
        notes: '',
        // DG specific fields
        dieselConsumption: 0,
        runningHours: 0,
        timeOfUseUnits: this.createTimeOfUseUnits(),
        timeOfUseDemand: this.createTimeOfUseDemand(),
        oilSurchargeRate: 0,
      },
      // Use centralized validation rules
      kvaDemandRules: VALIDATION_RULES.kvaDemand(),
      actualBillAmountRules: VALIDATION_RULES.actualBillAmount(),
      actualKwhRules: [
        (v) => v >= 0 || 'Actual kWh must be 0 or greater',
        (v) => !Number.isNaN(parseFloat(v)) || 'Must be a valid number',
      ],
      nonNegativeRules: [
        (v) => v === '' || v === null || v === undefined || parseFloat(v) >= 0 || 'Must be 0 or greater',
        (v) => v === '' || v === null || v === undefined || !Number.isNaN(parseFloat(v)) || 'Must be a valid number',
      ],
      dieselConsumptionRules: VALIDATION_RULES.dieselConsumption(),
      runningHoursRules: VALIDATION_RULES.runningHours(),
      notesRules: VALIDATION_RULES.notes(),
      timeOfUseDemandRatesLoadKey: '',
      timeOfUseDemandRatesLoadingKey: '',
    };
  },

  computed: {
    ...mapGetters('rateMaster', ['allRates']),

    // Get input configurations from centralized config
    inputConfig() {
      return INPUT_CONFIG;
    },

    billingMode() {
      return resolveBillingMode({
        activeCategories: this.costCategories,
        consumptionData: this.consumptionData,
        manualData: {
          timeOfUseUnits: this.formData.timeOfUseUnits,
          timeOfUseDemand: this.formData.timeOfUseDemand,
        },
      });
    },

    isTimeOfUseMode() {
      return isTimeOfUseBillingMode(this.billingMode);
    },

    shouldSplitDemandByTouPeriod() {
      return appConfig.getBillingCapabilities().splitDemandByTouPeriod === true;
    },

    showTimeOfUseDemandSplit() {
      return !this.isDGMode && this.isTimeOfUseMode && this.shouldSplitDemandByTouPeriod;
    },

    showSingleKvaDemandInput() {
      return !this.isDGMode && (!this.isTimeOfUseMode || !this.shouldSplitDemandByTouPeriod);
    },

    showManualKvaDemandCharges() {
      const activeChargeCodes = getActiveChargeCodes(this.costCategories);
      return !this.isDGMode
        && !this.isTimeOfUseMode
        && activeChargeCodes.some((chargeCode) => (
          MANUAL_KVA_DEMAND_CHARGE_CODES.includes(chargeCode)
        ));
    },

    timeOfUseUnitLines() {
      const lines = buildTimeOfUseUnitLines(this.costCategories);
      return lines;
    },

    timeOfUseDemandLines() {
      const lines = buildTimeOfUseDemandLines(this.costCategories);
      return lines.map((line) => ({
        ...line,
        rate: this.getCurrentTimeOfUseDemandRate(line),
      }));
    },

    timeOfUseTotalUnits() {
      return Object.entries(this.formData.timeOfUseUnits || {})
        .reduce((sum, [key, line]) => sum + this.getTimeOfUseLineUnits(key, line), 0);
    },

    timeOfUseMaxDemand() {
      return Object.values(this.formData.timeOfUseDemand || {})
        .reduce((max, line) => Math.max(max, parseFloat(line.kw) || 0), 0);
    },

    timeOfUseDemandChargeTotal() {
      return Object.entries(this.formData.timeOfUseDemand || {})
        .reduce((sum, [key, line]) => sum + this.getTimeOfUseDemandCharge(key, line), 0);
    },

    timeOfUseUnitChargeTotal() {
      return (this.formData.timeOfUseUnits ? Object
        .entries(this.formData.timeOfUseUnits) : []).reduce((sum, [key, line]) => {
        const lineUnits = this.getTimeOfUseLineUnits(key, line);
        const lineRate = this.getTimeOfUseLineRate(key, line);
        return sum + (lineUnits * lineRate);
      }, 0);
    },

    formattedKvaDemand() {
      return this.formData.kvaDemand
        ? parseFloat(this.formData.kvaDemand).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        : '0.00';
    },

    formattedActualAmount() {
      return this.formData.actualBillAmount
        ? parseFloat(this.formData.actualBillAmount).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        : '0.00';
    },

    formattedKvaDemandCharges() {
      const value = parseFloat(this.formData.kvaDemandCharges) || 0;

      return value > 0
        ? value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        : '0.00';
    },

    formattedActualKwh() {
      return this.formData.actualKwhFromBill
        ? parseFloat(this.formData.actualKwhFromBill).toLocaleString('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
        : '0.0';
    },

    formattedTimeOfUseTotalUnits() {
      return this.timeOfUseTotalUnits.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formattedTimeOfUseMaxDemand() {
      return this.timeOfUseMaxDemand.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formattedTimeOfUseDemandChargeTotal() {
      return this.timeOfUseDemandChargeTotal.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formattedOilSurchargeRate() {
      const rate = parseFloat(this.formData.oilSurchargeRate) || 0;
      return rate > 0
        ? rate.toLocaleString('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })
        : '';
    },

    formattedTimeOfUseUnitCharges() {
      return this.timeOfUseUnitChargeTotal.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // DG specific computed properties
    formattedDieselConsumption() {
      return this.formData.dieselConsumption
        ? parseFloat(this.formData.dieselConsumption).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        : '0.00';
    },

    formattedRunningHours() {
      return this.formData.runningHours
        ? parseFloat(this.formData.runningHours).toLocaleString('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
        : '0.0';
    },

    rateStateSignature() {
      return (this.allRates || []).map((rate) => [
        rate.id || rate._id || '',
        rate.costCategory || rate.cost_category || '',
        rate.rate,
        rate.effectiveFrom || rate.effective_from || '',
        rate.effectiveTo || rate.effective_to || '',
        rate.isActive !== undefined ? rate.isActive : rate.is_active,
      ].join(':')).join('|');
    },
  },

  watch: {
    billingPeriod: {
      handler() {
        this.loadTimeOfUseDemandRates();
      },
      immediate: true,
    },
    isTimeOfUseMode: {
      handler(isTimeOfUseMode) {
        if (isTimeOfUseMode) {
          this.syncTimeOfUseMasterFields();
          this.loadTimeOfUseDemandRates();
        }
      },
      immediate: true,
    },
    costCategories: {
      handler() {
        if (this.isTimeOfUseMode) {
          this.syncTimeOfUseMasterFields();
          this.loadTimeOfUseDemandRates(true);
        }
      },
      deep: true,
    },
    allRates: {
      handler() {
        if (this.isTimeOfUseMode) {
          this.loadTimeOfUseDemandRates(true);
        }
      },
      deep: true,
    },
    initialData: {
      handler(newData) {
        if (newData && this.editMode) {
          if (this.isDGMode) {
            // DG mode - use DG specific fields
            this.formData = {
              kvaDemand: 0, // Not used in DG mode
              actualBillAmount: 0, // Not used in DG mode
              kvaDemandCharges: 0,
              actualKwhFromBill: 0, // Not used in DG mode
              dieselConsumption: newData.dieselConsumption || 0,
              runningHours: newData.runningHours || 0,
              notes: newData.notes || '',
            };
          } else {
            // Electricity mode - use electricity specific fields
            this.resetElectricityFormData({
              kvaDemand: newData.kvaDemand || 0,
              actualBillAmount: newData.actualBillAmount || 0,
              kvaDemandCharges: newData.kvaDemandCharges || 0,
              actualKwhFromBill: newData.actualKwhFromBill || 0,
              timeOfUseUnits: this.normalizeTimeOfUseUnits(
                newData.timeOfUseUnits || {},
              ),
              timeOfUseDemand: this.normalizeTimeOfUseDemand(
                newData.timeOfUseDemand || {},
              ),
              oilSurchargeRate: parseFloat(newData.oilSurchargeRate) || 0,
              notes: newData.notes || '',
            });
          }
          this.$nextTick(() => {
            if (this.$refs.form) {
              this.$refs.form.validate();
            }
          });
        } else if (newData) {
          // Non-edit mode - use all available data
          this.formData = { ...this.formData, ...newData };
          this.validateAndEmit();
        }
      },
      immediate: true,
      deep: true,
    },
  },

  beforeDestroy() {
    this.timeOfUseDemandRatesLoadKey = '';
    this.timeOfUseDemandRatesLoadingKey = '';
  },

  methods: {
    ...mapActions('rateMaster', ['fetchRates']),

    createTimeOfUseUnits() {
      return {
        onPeak: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 4.1025,
        },
        offPeak: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 2.5849,
        },
        sunday: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 2.5849,
        },
        misc: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 3.3437,
        },
      };
    },

    createTimeOfUseDemand() {
      return {
        onPeak: { kw: 0, rate: 0 },
        offPeak: { kw: 0, rate: 0 },
        sunday: { kw: 0, rate: 0 },
      };
    },

    async loadTimeOfUseDemandRates() {
      if (!this.isTimeOfUseMode || !this.billingPeriod) {
        return;
      }

      const billingDate = this.getTimeOfUseRateDate();
      let loadKey = '';

      try {
        if (!this.allRates || this.allRates.length === 0) {
          await this.fetchRates();
        }

        loadKey = [
          this.billingPeriod.startDate || '',
          this.billingPeriod.endDate || '',
          this.billingMode,
          this.rateStateSignature,
        ].join('|');

        if (this.timeOfUseDemandRatesLoadKey === loadKey
        || this.timeOfUseDemandRatesLoadingKey === loadKey) {
          return;
        }

        this.timeOfUseDemandRatesLoadingKey = loadKey;
        const ratePairs = this.timeOfUseDemandLines.map((line) => [
          line.key,
          this.getEffectiveRateValueFromState([line.categoryCode], billingDate),
        ]);

        const rateMap = Object.fromEntries(ratePairs);
        const currentDemand = this.formData.timeOfUseDemand || this.createTimeOfUseDemand();
        const oilSurchargeRate = this.getEffectiveRateValueFromState(
          ['OIL_SURCHARGE'],
          billingDate,
        );

        this.formData.timeOfUseDemand = Object.fromEntries(
          Object.entries(currentDemand).map(([key, line]) => [
            key,
            {
              ...line,
              rate: rateMap[key] || 0,
            },
          ]),
        );
        this.formData.oilSurchargeRate = oilSurchargeRate;
        this.timeOfUseDemandRatesLoadKey = loadKey;
        this.validateAndEmit();
        this.timeOfUseDemandRatesLoadingKey = '';
      } catch (error) {
        console.error('Error loading time-of-use demand rates:', error);
      } finally {
        if (loadKey && this.timeOfUseDemandRatesLoadingKey === loadKey) {
          this.timeOfUseDemandRatesLoadingKey = '';
        }
      }
    },

    getEffectiveRateValueFromState(categoryCodes, billingDate) {
      const rateMaster = this.getEffectiveRateFromState(categoryCodes, billingDate);
      return rateMaster ? parseFloat(rateMaster.rate) || 0 : 0;
    },

    getCurrentTimeOfUseDemandRate(line = {}) {
      if (!line.categoryCode) {
        return 0;
      }

      return this.getEffectiveRateValueFromState(
        [line.categoryCode],
        this.getTimeOfUseRateDate(),
      );
    },

    getTimeOfUseRateDate() {
      return this.billingPeriod?.endDate
        ? new Date(this.billingPeriod.endDate)
        : new Date();
    },

    getEffectiveRateFromState(categoryCodes, billingDate) {
      const billingTime = billingDate.getTime();
      const categoryCodeSet = new Set(categoryCodes);
      const matchingRates = (this.allRates || [])
        .filter((rate) => {
          const costCategory = rate.costCategory || rate.cost_category;
          const isActive = rate.isActive !== false && rate.is_active !== false;
          return isActive && categoryCodeSet.has(costCategory);
        })
        .sort((a, b) => new Date(b.effectiveFrom || b.effective_from)
          - new Date(a.effectiveFrom || a.effective_from));

      const effectiveRate = matchingRates.find((rate) => {
        const effectiveFrom = new Date(rate.effectiveFrom || rate.effective_from);
        const effectiveTo = new Date(rate.effectiveTo || rate.effective_to);
        return effectiveFrom.getTime() <= billingTime
          && effectiveTo.getTime() >= billingTime;
      });
      const fallbackRates = effectiveRate
        ? [effectiveRate, ...matchingRates]
        : matchingRates;

      return fallbackRates.find((rate) => (
        (parseFloat(rate.rate) || 0) > 0
      )) || fallbackRates[0] || null;
    },

    syncTimeOfUseMasterFields() {
      const defaultUnits = this.createTimeOfUseUnits();
      const existingUnits = this.formData.timeOfUseUnits || {};
      const existingDemand = this.formData.timeOfUseDemand || {};

      this.formData.timeOfUseUnits = Object.fromEntries(
        this.timeOfUseUnitLines.map((line) => {
          const existingLine = existingUnits[line.key] || {};
          return [line.key, {
            ...(defaultUnits[line.key] || {}),
            ...existingLine,
            rate: parseFloat(existingLine.rate) || line.rate || 0,
          }];
        }),
      );

      this.formData.timeOfUseDemand = Object.fromEntries(
        this.timeOfUseDemandLines.map((line) => {
          const existingLine = existingDemand[line.key] || {};
          return [line.key, {
            ...existingLine,
            kw: parseFloat(existingLine.kw) || 0,
            rate: line.rate || 0,
          }];
        }),
      );
    },

    getTimeOfUseLineUnits(key, line = {}) {
      const difference = parseFloat(line.difference);
      const units = parseFloat(line.units);

      if (!Number.isNaN(difference)) {
        return Math.max(difference, 0);
      }

      if (!Number.isNaN(units)) {
        return Math.max(units, 0);
      }

      const finalReading = parseFloat(line.finalReading) || 0;
      const initialReading = parseFloat(line.initialReading) || 0;
      return Math.max(finalReading - initialReading, 0);
    },

    getTimeOfUseLineRate(key, line = {}) {
      const rate = parseFloat(line.rate);
      if (!Number.isNaN(rate)) {
        return rate;
      }

      const matchingLine = this.timeOfUseUnitLines.find((item) => item.key === key);
      return matchingLine ? matchingLine.rate : 0;
    },

    getTimeOfUseDemandRate(line = {}) {
      const rate = parseFloat(line.rate);
      return Number.isNaN(rate) ? 0 : rate;
    },

    syncTimeOfUseUnitLine(key) {
      const line = this.formData.timeOfUseUnits?.[key];

      if (!line) {
        return;
      }

      const units = Math.max(parseFloat(line.units) || 0, 0);

      this.$set(this.formData.timeOfUseUnits, key, {
        ...line,
        units,
        difference: units,
        rate: this.getTimeOfUseLineRate(key, line),
      });

      this.validateAndEmit();
    },

    formatTimeOfUseRate(rate) {
      return parseFloat(rate || 0).toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    },

    formatTimeOfUseDifference(key) {
      const line = this.formData.timeOfUseUnits?.[key] || {};
      return this.getTimeOfUseLineUnits(key, line).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatTimeOfUseUnits(key) {
      return this.formatTimeOfUseDifference(key);
    },

    formatTimeOfUseDemandTotal(key) {
      const line = this.formData.timeOfUseDemand?.[key] || {};
      const kw = Math.max(parseFloat(line.kw) || 0, 0);
      return kw.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getTimeOfUseDemandCharge(key, line = {}) {
      const kw = Math.max(parseFloat(line.kw) || 0, 0);
      const rate = this.getTimeOfUseDemandRate(line);
      return kw * rate;
    },

    formatTimeOfUseDemandCharge(key) {
      const line = this.formData.timeOfUseDemand?.[key] || {};
      return this.getTimeOfUseDemandCharge(key, line).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    normalizeTimeOfUseUnits(units = {}) {
      return Object.fromEntries(
        Object.entries(units).map(([key, line]) => {
          const hasFinalReading = line?.finalReading !== undefined && line?.finalReading !== null && line?.finalReading !== '';
          const hasInitialReading = line?.initialReading !== undefined && line?.initialReading !== null && line?.initialReading !== '';
          const finalReading = hasFinalReading ? (parseFloat(line.finalReading) || 0) : 0;
          const initialReading = hasInitialReading ? (parseFloat(line.initialReading) || 0) : 0;
          const explicitUnits = parseFloat(line?.units);
          const explicitDifference = parseFloat(line?.difference);
          const calculatedDifference = hasFinalReading && hasInitialReading
            ? Math.max(finalReading - initialReading, 0)
            : NaN;
          let unitsValue;
          if (!Number.isNaN(explicitUnits)) {
            unitsValue = Math.max(explicitUnits, 0);
          } else if (!Number.isNaN(explicitDifference)) {
            unitsValue = Math.max(explicitDifference, 0);
          } else if (!Number.isNaN(calculatedDifference)) {
            unitsValue = calculatedDifference;
          } else {
            unitsValue = 0;
          }
          const difference = !Number.isNaN(calculatedDifference) && calculatedDifference > 0
            ? calculatedDifference
            : unitsValue;
          const rate = this.getTimeOfUseLineRate(key, line);

          return [key, {
            ...line,
            finalReading,
            initialReading,
            difference,
            units: unitsValue,
            rate,
          }];
        }),
      );
    },

    hasSavedTimeOfUseTariffData() {
      const savedDemand = this.initialData?.timeOfUseDemand
        || {};
      const hasDemandRate = Object.values(savedDemand)
        .some((line) => (parseFloat(line?.rate) || 0) > 0);
      const hasOilSurchargeRate = this.initialData?.oilSurchargeRate !== undefined
        && this.initialData?.oilSurchargeRate !== null
        && this.initialData?.oilSurchargeRate !== '';

      return hasDemandRate && hasOilSurchargeRate;
    },

    normalizeTimeOfUseDemand(demand = {}) {
      return Object.fromEntries(
        Object.entries(demand).map(([key, line]) => {
          const kw = parseFloat(line?.kw) || 0;
          const rate = this.getTimeOfUseDemandRate(line);

          return [key, {
            ...line,
            kw,
            rate,
          }];
        }),
      );
    },

    getSubmissionKvaDemandCharges() {
      if (this.isTimeOfUseMode) {
        return this.timeOfUseDemandChargeTotal;
      }

      if (!this.showManualKvaDemandCharges) {
        return 0;
      }

      return parseFloat(this.formData.kvaDemandCharges) || 0;
    },

    resetElectricityFormData(overrides = {}) {
      this.formData = {
        kvaDemand: 0,
        actualBillAmount: 0,
        kvaDemandCharges: 0,
        actualKwhFromBill: 0,
        dieselConsumption: 0,
        runningHours: 0,
        notes: '',
        timeOfUseUnits: this.createTimeOfUseUnits(),
        timeOfUseDemand: this.createTimeOfUseDemand(),
        oilSurchargeRate: 0,
        ...overrides,
      };
    },

    validateAndEmit() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }

        const normalizedTimeOfUseUnits = this.normalizeTimeOfUseUnits(this.formData.timeOfUseUnits);
        const normalizedTimeOfUseDemand = this.normalizeTimeOfUseDemand(
          this.formData.timeOfUseDemand,
        );

        // Emit form data changes
        this.$emit('form-data-changed', {
          formData: {
            ...this.formData,
            timeOfUseUnits: normalizedTimeOfUseUnits,
            timeOfUseDemand: normalizedTimeOfUseDemand,
          },
          isValid: this.valid,
        });

        // Emit data-updated for edit mode
        if (this.editMode) {
          if (this.isDGMode) {
            // DG mode - emit DG specific data
            this.$emit('data-updated', {
              dieselConsumption: this.formData.dieselConsumption,
              runningHours: this.formData.runningHours,
              notes: this.formData.notes,
            });
          } else {
            // Electricity mode - emit electricity specific data
            const kvaDemand = this.isTimeOfUseMode
              ? this.timeOfUseMaxDemand
              : this.formData.kvaDemand;

            this.$emit('data-updated', {
              kvaDemand,
              actualBillAmount: this.formData.actualBillAmount,
              kvaDemandCharges: this.getSubmissionKvaDemandCharges(),
              actualKwhFromBill: this.formData.actualKwhFromBill,
              timeOfUseUnits: normalizedTimeOfUseUnits,
              timeOfUseDemand: normalizedTimeOfUseDemand,
              oilSurchargeRate: this.formData.oilSurchargeRate,
              notes: this.formData.notes,
            });
          }
        }
      });
    },

    submitForm() {
      if (!this.valid) {
        this.$emit('show-message', {
          type: 'error',
          message: 'Please complete all required fields correctly',
        });
        return;
      }

      let submissionData;

      if (this.isDGMode) {
        // DG mode validation and submission
        const dieselConsumption = parseFloat(this.formData.dieselConsumption);
        const runningHours = parseFloat(this.formData.runningHours);

        if (Number.isNaN(dieselConsumption) || dieselConsumption <= 0) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Invalid diesel consumption value',
          });
          return;
        }

        if (Number.isNaN(runningHours) || runningHours <= 0) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Invalid running hours value',
          });
          return;
        }

        submissionData = {
          dieselConsumption,
          runningHours,
          notes: this.formData.notes || '',
        };
      } else {
        // Electricity mode validation and submission
        const kvaDemand = parseFloat(this.formData.kvaDemand);
        const actualBillAmount = parseFloat(this.formData.actualBillAmount);

        if (Number.isNaN(kvaDemand) || kvaDemand < 0) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Invalid kVA demand value',
          });
          return;
        }

        if (Number.isNaN(actualBillAmount) || actualBillAmount < 0) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Invalid actual bill amount',
          });
          return;
        }

        const actualKwhFromBill = parseFloat(this.formData.actualKwhFromBill);

        if (Number.isNaN(actualKwhFromBill) || actualKwhFromBill < 0) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Invalid actual kWh from bill value',
          });
          return;
        }

        submissionData = {
          kvaDemand,
          actualBillAmount,
          kvaDemandCharges: this.getSubmissionKvaDemandCharges(),
          actualKwhFromBill,
          timeOfUseUnits: this.normalizeTimeOfUseUnits(this.formData.timeOfUseUnits),
          timeOfUseDemand: this.normalizeTimeOfUseDemand(this.formData.timeOfUseDemand),
          oilSurchargeRate: parseFloat(this.formData.oilSurchargeRate) || 0,
          notes: this.formData.notes || '',
        };

        if (this.isTimeOfUseMode) {
          submissionData.kvaDemand = this.timeOfUseMaxDemand;
        }
      }

      this.$emit('form-submitted', submissionData);

      this.$emit('show-message', {
        type: 'success',
        message: `${this.isDGMode ? 'DG' : 'Manual'} data entry completed successfully`,
      });
    },

    resetForm() {
      if (this.isDGMode) {
        this.formData = {
          kvaDemand: 0,
          actualBillAmount: 0,
          kvaDemandCharges: 0,
          actualKwhFromBill: 0,
          dieselConsumption: 0,
          runningHours: 0,
          notes: '',
        };
      } else {
        this.resetElectricityFormData();
      }

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.$emit('form-reset');

      this.$emit('show-message', {
        type: 'info',
        message: 'Form has been reset',
      });
    },

    // Public method to get current form data
    getFormData() {
      return {
        formData: { ...this.formData },
        isValid: this.valid,
      };
    },

    // Public method to set form data
    setFormData(data) {
      this.formData = { ...data };
      this.validateAndEmit();
    },
  },
};
</script>

<style scoped>
.v-text-field >>> input[type="number"] {
  -moz-appearance: textfield;
}

.v-text-field >>> input[type="number"]::-webkit-outer-spin-button,
.v-text-field >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.time-of-use-consumption-card {
  border-radius: 12px;
  overflow: hidden;
}

.time-of-use-demand-card {
  border-radius: 12px;
  overflow: hidden;
}

.time-of-use-consumption-table >>> table {
  width: 100%;
}

.time-of-use-demand-table >>> table {
  width: 100%;
}

.time-of-use-consumption-table >>> th,
.time-of-use-consumption-table >>> td {
  padding: 12px 10px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.time-of-use-demand-table >>> th,
.time-of-use-demand-table >>> td {
  padding: 12px 10px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.time-of-use-consumption-table >>> th {
  background: #f4f6fb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.time-of-use-demand-table >>> th {
  background: #f4f6fb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.time-of-use-consumption-table >>> tbody tr:nth-child(even) {
  background: #fafbff;
}

.time-of-use-demand-table >>> tbody tr:nth-child(even) {
  background: #fafbff;
}

.unit-label-cell {
  min-width: 220px;
}

.time-of-use-table-input {
  max-width: 160px;
  margin: 0 auto;
}

.time-of-use-total-row {
  background: linear-gradient(90deg, rgba(210, 207, 255, 0.55), rgba(225, 255, 226, 0.55));
  border-radius: 10px;
  padding: 14px 16px;
}

.time-of-use-total-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 4px;
  background: #d6d2ff;
  font-weight: 700;
  letter-spacing: 0.04em;
}
</style>
